import React from 'react'

import { useAiOperatorFeedback } from '../hooks/useAiOperatorFeedback'
import { FiThumbsDown, FiThumbsUp } from 'react-icons/fi'

import {
  Box,
  Button,
  Flex,
  Icon,
  Image,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react'

import {
  AiOperatorFeedbackIProps,
  BodyInitialIProps,
  BodySubmittedIProps,
  HeaderInitialIProps,
} from '../types'
import { FEEDBACK_CONSTANTS } from '../utils'

/**
 * HeaderInitial component - Displays the initial header with feedback buttons
 *
 * @param headerText - Main header text
 * @param subHeaderText - Subheader text
 * @param feedbackType - Currently selected feedback type
 * @param onThumbClick - Handler for thumb button clicks
 */
const HeaderInitial = ({
  headerText,
  subHeaderText,
  feedbackType,
  onThumbClick,
}: HeaderInitialIProps) => {
  return (
    <Flex
      align='center'
      aria-label={FEEDBACK_CONSTANTS.ARIA_LABELS.FEEDBACK_FORM}
      justify='space-between'
      minHeight={FEEDBACK_CONSTANTS.DIMENSIONS.MIN_HEIGHT}
      role='group'
    >
      <Flex align='center' gap={3}>
        <Image
          alt={FEEDBACK_CONSTANTS.ARIA_LABELS.AI_OPERATOR_ICON}
          flexShrink={0}
          height={FEEDBACK_CONSTANTS.DIMENSIONS.BUTTON_SIZE}
          src='/ai-operator-icon.svg'
          width='auto'
        />
        <Box>
          <Text
            color={FEEDBACK_CONSTANTS.COLORS.GRAY_600}
            fontSize='sm'
            fontWeight='medium'
          >
            {headerText}
          </Text>
          <Text color={FEEDBACK_CONSTANTS.COLORS.GRAY_600} fontSize='sm'>
            {subHeaderText}
          </Text>
        </Box>
      </Flex>

      <Flex gap={2}>
        <Button
          _focus={{ outline: 'none' }}
          _hover={{
            transform: 'scale(1.05)',
            bg: FEEDBACK_CONSTANTS.COLORS.POSITIVE_BG,
          }}
          alignItems='center'
          aria-label={FEEDBACK_CONSTANTS.ARIA_LABELS.POSITIVE_BUTTON}
          aria-pressed={feedbackType === 'positive'}
          bg={
            feedbackType === 'positive'
              ? FEEDBACK_CONSTANTS.COLORS.POSITIVE_BG
              : FEEDBACK_CONSTANTS.COLORS.WHITE
          }
          borderRadius='full'
          boxShadow='sm'
          display='flex'
          h={FEEDBACK_CONSTANTS.DIMENSIONS.BUTTON_SIZE}
          justifyContent='center'
          minW='auto'
          onClick={() => onThumbClick('positive')}
          p={2}
          size='sm'
          variant='unstyled'
          w={FEEDBACK_CONSTANTS.DIMENSIONS.BUTTON_SIZE}
        >
          <Icon
            as={FiThumbsUp}
            boxSize={FEEDBACK_CONSTANTS.DIMENSIONS.ICON_SIZE}
            color={
              feedbackType === 'positive'
                ? FEEDBACK_CONSTANTS.COLORS.POSITIVE_TEXT
                : FEEDBACK_CONSTANTS.COLORS.GRAY_500
            }
          />
        </Button>
        <Button
          _focus={{ outline: 'none' }}
          _hover={{
            transform: 'scale(1.05)',
            bg: FEEDBACK_CONSTANTS.COLORS.NEGATIVE_BG,
          }}
          alignItems='center'
          aria-label={FEEDBACK_CONSTANTS.ARIA_LABELS.NEGATIVE_BUTTON}
          aria-pressed={feedbackType === 'negative'}
          bg={
            feedbackType === 'negative'
              ? FEEDBACK_CONSTANTS.COLORS.NEGATIVE_BG
              : FEEDBACK_CONSTANTS.COLORS.WHITE
          }
          borderRadius='full'
          boxShadow='sm'
          display='flex'
          h={FEEDBACK_CONSTANTS.DIMENSIONS.BUTTON_SIZE}
          justifyContent='center'
          minW='auto'
          onClick={() => onThumbClick('negative')}
          p={2}
          size='sm'
          variant='brand.900'
          w={FEEDBACK_CONSTANTS.DIMENSIONS.BUTTON_SIZE}
        >
          <Icon
            as={FiThumbsDown}
            boxSize={FEEDBACK_CONSTANTS.DIMENSIONS.ICON_SIZE}
            color={
              feedbackType === 'negative'
                ? FEEDBACK_CONSTANTS.COLORS.NEGATIVE_TEXT
                : FEEDBACK_CONSTANTS.COLORS.GRAY_500
            }
          />
        </Button>
      </Flex>
    </Flex>
  )
}

/**
 * HeaderSubmitted component - Displays the header after feedback submission
 */
const HeaderSubmitted = () => (
  <Flex
    align='center'
    justify='space-between'
    minHeight={FEEDBACK_CONSTANTS.DIMENSIONS.MIN_HEIGHT}
  >
    <Flex align='center' gap={3}>
      <Box>
        <Text
          color={FEEDBACK_CONSTANTS.COLORS.GRAY_600}
          fontSize='sm'
          fontWeight='medium'
        >
          {FEEDBACK_CONSTANTS.MESSAGES.THANK_YOU}
        </Text>
      </Box>
    </Flex>
  </Flex>
)

/**
 * BodyInitial component - Displays the feedback form
 *
 * @param feedbackMessage - Current feedback message text
 * @param setFeedbackMessage - Function to update feedback message
 * @param placeholderText - Placeholder text for textarea
 * @param isSubmitting - Whether form is currently submitting
 * @param onSubmit - Submit handler function
 */
const BodyInitial = ({
  feedbackMessage,
  setFeedbackMessage,
  placeholderText,
  isSubmitting,
  onSubmit,
}: BodyInitialIProps) => {
  const isSubmitDisabled = isSubmitting

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.target.value.length <= FEEDBACK_CONSTANTS.MAX_MESSAGE_LENGTH) {
      setFeedbackMessage(e.target.value)
    }
  }

  return (
    <Box
      bg={FEEDBACK_CONSTANTS.COLORS.WHITE}
      borderBottomRadius={FEEDBACK_CONSTANTS.DIMENSIONS.BORDER_RADIUS}
      p={4}
    >
      <VStack align='stretch' spacing={3}>
        <Text
          color={FEEDBACK_CONSTANTS.COLORS.GRAY_600}
          fontSize='sm'
          fontWeight='medium'
        >
          {FEEDBACK_CONSTANTS.MESSAGES.SPECIFY_TEXT}
        </Text>

        <Box>
          <Textarea
            _disabled={{
              opacity: 0.6,
              cursor: 'not-allowed',
            }}
            aria-label={FEEDBACK_CONSTANTS.ARIA_LABELS.FEEDBACK_TEXTAREA}
            fontSize='sm'
            isDisabled={isSubmitting}
            onChange={handleTextareaChange}
            placeholder={placeholderText}
            resize='none'
            rows={3}
            value={feedbackMessage}
          />
        </Box>

        <Flex align='center' justify='flex-end'>
          <Button
            _disabled={{
              opacity: 0.4,
              cursor: 'not-allowed',
              _hover: {
                opacity: 0.4,
              },
            }}
            _hover={{
              bg: 'brand.200',
              opacity: 0.8,
            }}
            aria-label={FEEDBACK_CONSTANTS.ARIA_LABELS.SUBMIT_BUTTON}
            bg='brand.200'
            borderRadius='lg'
            color={FEEDBACK_CONSTANTS.COLORS.WHITE}
            isDisabled={isSubmitDisabled}
            isLoading={isSubmitting}
            loadingText={FEEDBACK_CONSTANTS.MESSAGES.LOADING_TEXT}
            onClick={onSubmit}
            px='18px'
            py='10px'
            size='md'
          >
            {FEEDBACK_CONSTANTS.MESSAGES.SUBMIT_BUTTON}
          </Button>
        </Flex>
      </VStack>
    </Box>
  )
}

/**
 * BodySubmitted component - Displays success message after feedback submission
 *
 * @param feedbackType - Type of feedback submitted (positive or negative)
 */
const BodySubmitted = ({ feedbackType }: BodySubmittedIProps) => {
  const message =
    feedbackType === 'positive'
      ? FEEDBACK_CONSTANTS.MESSAGES.POSITIVE_SUCCESS
      : FEEDBACK_CONSTANTS.MESSAGES.NEGATIVE_SUCCESS

  return (
    <Box
      aria-live='polite'
      bg={FEEDBACK_CONSTANTS.COLORS.SUCCESS_BG}
      borderBottomRadius={FEEDBACK_CONSTANTS.DIMENSIONS.BORDER_RADIUS}
      p={4}
      role='status'
    >
      <Text
        color={FEEDBACK_CONSTANTS.COLORS.POSITIVE_SUCCESS_TEXT}
        fontSize='sm'
        fontWeight='medium'
      >
        {message}
      </Text>
    </Box>
  )
}

/**
 * AiOperatorFeedback component - Main component for collecting AI operator feedback
 *
 * @param incident - The incident data from GraphQL query
 */
const AiOperatorFeedbackComponent = ({
  incident,
}: AiOperatorFeedbackIProps) => {
  const {
    isExpanded,
    feedbackType,
    feedbackMessage,
    isSubmitting,
    isSubmitted,
    shouldShow,
    isFlagged,
    handleThumbClick,
    handleSubmit,
    setFeedbackMessage,
  } = useAiOperatorFeedback({ incident })
  const headerText = isFlagged
    ? FEEDBACK_CONSTANTS.MESSAGES.HEADER_FLAGGED
    : FEEDBACK_CONSTANTS.MESSAGES.HEADER_RESOLVED
  const subHeaderText = isFlagged
    ? FEEDBACK_CONSTANTS.MESSAGES.SUBHEADER_FLAGGED
    : FEEDBACK_CONSTANTS.MESSAGES.SUBHEADER_RESOLVED
  const placeholderText =
    feedbackType === 'positive'
      ? FEEDBACK_CONSTANTS.MESSAGES.PLACEHOLDER_POSITIVE
      : FEEDBACK_CONSTANTS.MESSAGES.PLACEHOLDER_NEGATIVE

  if (!shouldShow) {
    return null
  }

  return (
    <Box
      aria-label='AI Operator Feedback'
      bg={FEEDBACK_CONSTANTS.COLORS.CONTAINER_BG}
      border={`1px solid ${FEEDBACK_CONSTANTS.COLORS.BORDER}`}
      borderRadius={FEEDBACK_CONSTANTS.DIMENSIONS.BORDER_RADIUS}
      mt={4}
      mx={6}
      role='region'
    >
      {/* Header Section */}
      <Box
        borderBottom={
          isExpanded ? `1px solid ${FEEDBACK_CONSTANTS.COLORS.BORDER}` : 'none'
        }
        px={4}
        py={2}
      >
        {isSubmitted ? (
          <HeaderSubmitted />
        ) : (
          <HeaderInitial
            feedbackType={feedbackType}
            headerText={headerText}
            onThumbClick={handleThumbClick}
            subHeaderText={subHeaderText}
          />
        )}
      </Box>

      {/* Body Section */}
      {(isExpanded || isSubmitted) &&
        (isSubmitted && feedbackType ? (
          <BodySubmitted feedbackType={feedbackType} />
        ) : (
          <BodyInitial
            feedbackMessage={feedbackMessage}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
            placeholderText={placeholderText}
            setFeedbackMessage={setFeedbackMessage}
          />
        ))}
    </Box>
  )
}

export const AiOperatorFeedback = React.memo(AiOperatorFeedbackComponent)
